// Pure helpers for rendering a state's Medicaid config as a Word-openable
// document. Kept dependency-free (no MUI/React) so it stays unit-testable and
// can be shared with ManageMedicaidVariables.jsx.
//
// We emit an HTML document with a Word MIME type / .doc extension rather than a
// real .docx. This opens correctly in Microsoft Word and needs zero new
// dependencies. ponytail: HTML-as-.doc has a ceiling (no native .docx styling
// primitives); upgrade path is the `docx` npm package if richer formatting is
// ever required.

// Internal/system fields never shown to the user.
const EXCLUDED_FIELDS = ['_id', 'state', 'create_date', 'modified_date', '__v'];

// Convert snake_case to Title Case (e.g. "single_asset_limit" -> "Single Asset Limit").
function formatLabel(key) {
  return key
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

// Get all context section keys from the data (keys starting with 'context_').
function getContextKeys(data) {
  if (!data) return [];
  return Object.keys(data).filter((key) => key.startsWith('context_'));
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

// Build a Word-openable HTML document string for a single state's config.
function buildMedicaidDocHtml(data) {
  if (!data) return '';

  const stateName = data.name || data.state || 'Medicaid';
  const rows = [`<h1>${escapeHtml(stateName)} Medicaid Configuration</h1>`];

  if (data.state) {
    rows.push(`<p><b>State:</b> ${escapeHtml(data.state)}</p>`);
  }
  if (data.active !== undefined) {
    rows.push(`<p><b>Status:</b> ${data.active === false ? 'Coming soon' : 'Active'}</p>`);
  }

  const staticVars = data.static_variables || {};
  const staticKeys = Object.keys(staticVars).filter((k) => !EXCLUDED_FIELDS.includes(k));
  if (staticKeys.length > 0) {
    rows.push('<h2>Static Variables</h2>');
    rows.push('<table border="1" cellspacing="0" cellpadding="4">');
    staticKeys.forEach((key) => {
      rows.push(
        `<tr><td><b>${escapeHtml(formatLabel(key))}</b></td><td>${escapeHtml(staticVars[key])}</td></tr>`
      );
    });
    rows.push('</table>');
  }

  getContextKeys(data).forEach((contextKey) => {
    rows.push(`<h2>${escapeHtml(formatLabel(contextKey.replace('context_', '')))}</h2>`);
    const section = data[contextKey] || {};
    Object.keys(section)
      .filter((fieldKey) => !EXCLUDED_FIELDS.includes(fieldKey))
      .forEach((fieldKey) => {
        rows.push(`<h3>${escapeHtml(formatLabel(fieldKey))}</h3>`);
        rows.push(`<p>${escapeHtml(section[fieldKey]).replace(/\n/g, '<br/>')}</p>`);
      });
  });

  return (
    '<!DOCTYPE html>' +
    '<html xmlns:o="urn:schemas-microsoft-com:office:office" ' +
    'xmlns:w="urn:schemas-microsoft-com:office:word" ' +
    'xmlns="http://www.w3.org/TR/REC-html40">' +
    `<head><meta charset="utf-8"><title>${escapeHtml(stateName)}</title></head>` +
    `<body>${rows.join('')}</body></html>`
  );
}

module.exports = {
  EXCLUDED_FIELDS,
  formatLabel,
  getContextKeys,
  buildMedicaidDocHtml,
};
